import { Send, Upload } from "lucide-react";
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { twMerge } from "tailwind-merge";

import { Button } from "@/components/button";

const MAX_TEXTAREA_HEIGHT = 200;

type MBCChatInputProps = {
  isDisabled: boolean;
  onAttach?: () => void;
  onSend: (text: string) => void;
};

export function MBCChatInput({
  isDisabled,
  onAttach,
  onSend,
}: MBCChatInputProps) {
  const [input, setInput] = useState("");
  const containerRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const canSend = !isDisabled && input.trim().length > 0;

  useLayoutEffect(() => {
    const textarea = textareaRef.current;

    if (!textarea) return;
    textarea.style.height = "auto";
    textarea.style.height = `${Math.min(textarea.scrollHeight, MAX_TEXTAREA_HEIGHT)}px`;
  }, [input]);

  useEffect(() => {
    if (!isDisabled) focusTextareaUnlessElsewhere();
  }, [isDisabled]);

  function focusTextareaUnlessElsewhere() {
    const active = document.activeElement;
    const focusIsFree =
      active === document.body || containerRef.current?.contains(active);

    if (focusIsFree && !document.querySelector("[role=dialog]")) {
      textareaRef.current?.focus();
    }
  }

  function handleSubmit() {
    const trimmed = input.trim();

    if (!trimmed || isDisabled) return;
    onSend(trimmed);
    setInput("");
    textareaRef.current?.focus();
  }

  return (
    <div
      className="border-t border-border-secondary bg-bg-primary px-md py-lg lg:px-3xl"
      ref={containerRef}
    >
      <div className="flex items-end gap-md">
        <div className="flex flex-1 items-end gap-sm rounded-xl border border-border-primary bg-bg-primary px-lg py-sm">
          <textarea
            className="min-h-[40px] flex-1 resize-none overflow-y-auto bg-transparent py-sm text-md text-text-primary outline-none placeholder:text-text-placeholder"
            onChange={(event) => setInput(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Enter" && !event.shiftKey) {
                event.preventDefault();
                handleSubmit();
              }
            }}
            placeholder="Type your answer..."
            ref={textareaRef}
            rows={1}
            value={input}
          />

          <Button
            aria-label="Upload assets"
            className="text-text-secondary hover:text-text-primary"
            isDisabled={isDisabled || !onAttach}
            isIconOnly
            onPress={onAttach}
            size="sm"
            variant="unstyled"
          >
            <Upload className="size-5" />
          </Button>
        </div>

        <Button
          aria-label="Send message"
          className={twMerge(
            "flex size-12 shrink-0 items-center justify-center rounded-lg bg-bg-secondary text-fg-quaternary transition-colors",
            canSend && "text-fg-brand-primary hover:bg-bg-primary-hover",
          )}
          isDisabled={!canSend}
          isIconOnly
          onPress={handleSubmit}
          variant="unstyled"
        >
          <Send className="size-5" />
        </Button>
      </div>
    </div>
  );
}
